<template>
  <div class="content">
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-8 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">{{ $t("shipped_no_deduct.title") }}</h6>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--6">
      <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2" v-loading="loading">
        <template slot="header">
          <p class="text-muted small mb-3">{{ $t("shipped_no_deduct.hint") }}</p>

          <!-- Filter row (§5.2; #441 owner 10-09). One line at >= 1280px:
               date · platform · status · ค้นหา · ส่งออก Excel · รีเฟรช. Buttons
               are bottom-aligned and as tall as the el-select (.snd-btn).
               Date / platform / status refetch only on ค้นหา. No shop filter. -->
          <div class="snd-filters">
            <div class="snd-field snd-field--date">
              <label class="form-control-label">{{ $t("shipped_no_deduct.filter_date") }}</label>
              <el-date-picker
                v-model="dateRange"
                type="daterange"
                size="small"
                class="snd-date"
                :clearable="false"
                :editable="false"
                value-format="yyyy-MM-dd"
                format="yyyy-MM-dd"
                range-separator="-"
                :picker-options="pickerOptions"
              ></el-date-picker>
            </div>
            <div class="snd-field">
              <label class="form-control-label">{{ $t("shipped_no_deduct.filter_platform") }}</label>
              <el-select
                v-model="platformIds"
                multiple
                collapse-tags
                size="small"
                class="w-100"
                :placeholder="$t('shipped_no_deduct.all')"
              >
                <el-option v-for="id in platformOptions" :key="id" :label="platformName(id)" :value="id"></el-option>
              </el-select>
            </div>
            <div class="snd-field">
              <label class="form-control-label">{{ $t("shipped_no_deduct.filter_status") }}</label>
              <el-select
                v-model="statuses"
                multiple
                collapse-tags
                size="small"
                class="w-100"
                :placeholder="$t('shipped_no_deduct.all')"
              >
                <el-option v-for="s in statusOptions" :key="s" :label="statusName(s)" :value="s"></el-option>
              </el-select>
            </div>
            <div class="snd-actions">
              <base-button type="primary" size="sm" class="snd-btn" @click="search()">
                <i class="fa fa-search"></i> {{ $t("search") }}
              </base-button>
              <base-button type="success" size="sm" class="snd-btn" :disabled="!filteredOrders.length" @click="exportExcel()">
                <i class="fas fa-file-excel"></i> {{ $t("export_excel") }}
              </base-button>
              <base-button type="secondary" size="sm" class="snd-btn" :disabled="loading" @click="search()">
                <i class="fas fa-sync-alt"></i> {{ $t("refresh") }}
              </base-button>
            </div>
          </div>

          <div class="d-flex align-items-center flex-wrap snd-subbar">
            <small class="text-muted">{{ $t("shipped_no_deduct.date_hint") }}</small>
            <small class="ml-auto text-muted" v-if="generatedAt">{{ $t("shipped_no_deduct.as_of", { time: asOfTime }) }}</small>
          </div>

          <!-- Banners (§5.3) -->
          <div v-if="errorMessage" class="alert alert-danger d-flex align-items-center mt-3 mb-0">
            <span>{{ errorMessage }}</span>
            <base-button type="secondary" size="sm" class="ml-auto" @click="search()">{{ $t("shipped_no_deduct.retry") }}</base-button>
          </div>
          <div v-if="missingCount > 0" class="alert alert-warning d-flex align-items-center mt-3 mb-0">
            <span>{{ $t("shipped_no_deduct.mapping_missing_banner", { n: missingCount }) }}</span>
            <a href="#" class="ml-3 font-weight-bold snd-link" @click.prevent="showMappingMissing()">
              {{ $t("shipped_no_deduct.mapping_missing_link") }}
            </a>
          </div>
        </template>

        <el-tabs v-model="activeTab" class="px-2">
          <!-- Tab สรุปตามสินค้า: product × channel pivot (§8). One qty column per
               platform present in the result, fixed order; 0 is drawn as "–". -->
          <el-tab-pane name="summary" :label="`${$t('shipped_no_deduct.tab_summary')} (${pivot.rows.length})`">
            <el-table
              class="snd-pivot"
              :data="pivot.rows"
              row-key="wms_product_id"
              header-row-class-name="thead-light"
              show-summary
              :summary-method="summaryFooter"
              :empty-text="$t('shipped_no_deduct.empty')"
            >
              <el-table-column :label="$t('shipped_no_deduct.col_code')" prop="code" width="120" class-name="snd-nowrap"></el-table-column>
              <el-table-column :label="$t('shipped_no_deduct.col_name')" min-width="200" class-name="snd-word-wrap">
                <template slot-scope="{ row }">
                  {{ row.name }}
                  <span v-if="row.product_deleted" class="text-muted">{{ $t("shipped_no_deduct.product_deleted") }}</span>
                </template>
              </el-table-column>
              <el-table-column
                v-for="p in pivot.platforms"
                :key="'pf-' + p"
                :label="platformName(p)"
                min-width="90"
                align="right"
                class-name="snd-nowrap"
              >
                <template slot-scope="{ row }">
                  <span v-if="row.by_platform[p]">{{ fmtNum(row.by_platform[p]) }}</span>
                  <span v-else class="text-muted">–</span>
                </template>
              </el-table-column>
              <el-table-column :label="$t('shipped_no_deduct.total')" prop="quantity" width="96" align="right" class-name="snd-nowrap">
                <template slot-scope="{ row }"><b>{{ fmtNum(row.quantity) }}</b></template>
              </el-table-column>
              <el-table-column :label="$t('shipped_no_deduct.col_order_count')" prop="order_count" width="120" align="right" class-name="snd-nowrap">
                <template slot-scope="{ row }">{{ fmtNum(row.order_count) }}</template>
              </el-table-column>
            </el-table>
          </el-tab-pane>

          <!-- Tab รายการออเดอร์ -->
          <el-tab-pane name="orders" :label="`${$t('shipped_no_deduct.tab_orders')} (${orderRows.length})`">
            <div v-if="mappingOnly" class="d-flex align-items-center mb-2 small">
              <span class="text-warning font-weight-bold">{{ $t("shipped_no_deduct.mapping_missing_only") }}</span>
              <a href="#" class="ml-2 snd-link" @click.prevent="mappingOnly = false">{{ $t("shipped_no_deduct.clear_filter") }}</a>
            </div>

            <el-table
              class="snd-orders"
              :data="pagedOrders"
              row-key="id"
              header-row-class-name="thead-light"
              :empty-text="$t('shipped_no_deduct.empty')"
            >
              <el-table-column :label="$t('shipped_no_deduct.col_order_id')" width="88">
                <template slot-scope="{ row }">
                  <router-link :to="`/backoffice/orders/${row.id}`">{{ row.id }}</router-link>
                </template>
              </el-table-column>
              <el-table-column :label="$t('shipped_no_deduct.col_platform_order_id')" min-width="130">
                <template slot-scope="{ row }">
                  <div>{{ row.platform_order_id }}</div>
                  <div v-if="row.platform_package_id" class="small text-muted">{{ row.platform_package_id }}</div>
                </template>
              </el-table-column>
              <el-table-column :label="$t('shipped_no_deduct.col_platform')" width="84" class-name="snd-word-wrap">
                <template slot-scope="{ row }">{{ platformName(row.platform_id) }}</template>
              </el-table-column>
              <el-table-column :label="$t('shipped_no_deduct.col_shop')" prop="shop_name" min-width="110" class-name="snd-word-wrap"></el-table-column>
              <el-table-column :label="$t('shipped_no_deduct.col_status')" width="110" class-name="snd-word-wrap">
                <template slot-scope="{ row }">
                  <div>{{ statusName(row.order_status) }}</div>
                  <el-tag v-if="Number(row.return_status) === 1" type="warning" size="mini">
                    {{ $t("shipped_no_deduct.returning_badge") }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column :label="$t('shipped_no_deduct.col_created_at')" prop="created_at" width="150" class-name="snd-nowrap"></el-table-column>
              <el-table-column :label="$t('shipped_no_deduct.col_reason')" width="140">
                <template slot-scope="{ row }">
                  <el-tag :type="row.reason === 'closed_without_items' ? 'warning' : 'info'" size="small" class="snd-wrap-tag">
                    {{ $t(reasonKey(row.reason)) }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column :label="$t('shipped_no_deduct.col_items')" min-width="240" class-name="snd-word-wrap">
                <template slot-scope="{ row }">
                  <el-tag v-if="row.mapping_missing || !(row.items || []).length" type="danger" size="small">
                    {{ $t("shipped_no_deduct.mapping_missing") }}
                  </el-tag>
                  <div v-else v-for="(it, i) in row.items" :key="i" class="small">
                    <b>{{ it.code }}</b> {{ it.name }}
                    <span v-if="it.product_deleted" class="text-muted">{{ $t("shipped_no_deduct.product_deleted") }}</span>
                    × {{ fmtNum(it.quantity) }}
                  </div>
                </template>
              </el-table-column>
            </el-table>

            <div class="d-flex justify-content-center justify-content-sm-between flex-wrap mt-3" v-if="orderRows.length">
              <p class="card-category">
                {{ $t("data") }} {{ pageFrom + 1 }} {{ $t("to") }} {{ pageTo }} {{ $t("of") }} {{ orderRows.length }}
              </p>
              <base-pagination
                class="pagination-no-border"
                v-model="currentPage"
                :per-page="pageSize"
                :total="orderRows.length"
              ></base-pagination>
            </div>
          </el-tab-pane>
        </el-tabs>
      </card>
    </div>

  </div>
</template>

<script>
// #437 / #439 — สินค้าที่ส่งแล้วแต่ยังไม่ตัดสต็อก.
// Spec: mdf-project docs/specs/2026-10-09-wms-shipped-without-stock-deduct-report-design.md
// §5 + §8 owner revision: no ack (manual stock marking) at all — every row is
// always shown; the summary is a product × channel pivot.
// Ruling R4: one GET returns every order row; filtering, the summary and the
// xlsx export are client-side (src/utils/shippedNoDeduct.js, pinned by jest).
// Keep-alive: no refetch on activation — filters and results persist; the
// user refreshes with รีเฟรช (shows "ข้อมูล ณ HH:mm").
import { mapActions } from "vuex";
import {
  Table, TableColumn, Tabs, TabPane, Select, Option, DatePicker, Tag, Loading
} from "element-ui";
import { BasePagination } from "@/components";
import { utils, writeFile } from "xlsx";
import { apiErrorMessage } from "@/util/api-error";
import {
  SND_STATUSES, SND_PLATFORM_IDS, SND_STATUS_LABEL_KEYS, SND_MAX_DAYS, SND_PAGE_SIZE,
  filterOrders, summarizeByProductAndPlatform, reasonLabelKey,
  platformLabel, mappingMissingCount, isRangeAllowed, defaultShippedNoDeductRange,
  buildQuery, responseData, errorKey, summarySheetRows, orderSheetRows, exportFileName
} from "@/utils/shippedNoDeduct";

const DAY_MS = 24 * 60 * 60 * 1000;

export default {
  name: "WmsShippedNoDeduct",
  components: {
    BasePagination,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Tabs.name]: Tabs,
    [TabPane.name]: TabPane,
    [Select.name]: Select,
    [Option.name]: Option,
    [DatePicker.name]: DatePicker,
    [Tag.name]: Tag
  },
  directives: {
    loading: Loading.directive
  },
  data() {
    const vm = this;
    return {
      // #441: yesterday -> today, Bangkok calendar days.
      dateRange: defaultShippedNoDeductRange(new Date()),
      platformIds: [],
      statuses: SND_STATUSES.slice(),
      mappingOnly: false,
      activeTab: "summary",
      orders: [],
      generatedAt: "",
      loaded: false,
      loading: false,
      errorMessage: "",
      currentPage: 1,
      pageSize: SND_PAGE_SIZE,
      pickMin: null,
      platformOptions: SND_PLATFORM_IDS,
      statusOptions: SND_STATUSES,
      pickerOptions: {
        // Block a pick that would make the span exceed SND_MAX_DAYS (inclusive).
        onPick({ minDate, maxDate }) {
          vm.pickMin = maxDate ? null : minDate;
        },
        disabledDate(d) {
          if (!vm.pickMin) return false;
          const diff = Math.abs(d.getTime() - vm.pickMin.getTime());
          return diff > (SND_MAX_DAYS - 1) * DAY_MS;
        }
      }
    };
  },
  computed: {
    // §8: no ack view — every row. #441: no shop filter (no shopIds).
    filteredOrders() {
      return filterOrders(this.orders, { view: "all" });
    },
    orderRows() {
      return this.mappingOnly ? this.filteredOrders.filter(o => o.mapping_missing) : this.filteredOrders;
    },
    pivot() {
      return summarizeByProductAndPlatform(this.filteredOrders);
    },
    missingCount() {
      return mappingMissingCount(this.filteredOrders);
    },
    pageFrom() {
      return (this.currentPage - 1) * this.pageSize;
    },
    pageTo() {
      return Math.min(this.pageFrom + this.pageSize, this.orderRows.length);
    },
    pagedOrders() {
      return this.orderRows.slice(this.pageFrom, this.pageTo);
    },
    asOfTime() {
      const d = new Date(this.generatedAt);
      if (Number.isNaN(d.getTime())) return this.generatedAt;
      return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    }
  },
  watch: {
    filteredOrders() {
      this.currentPage = 1;
    },
    mappingOnly() {
      this.currentPage = 1;
    }
  },
  mounted() {
    this.search();
  },
  methods: {
    ...mapActions({
      getShippedNoDeduct: "WmsInventory/getShippedNoDeduct"
    }),
    fmtNum(n) {
      return (Number(n) || 0).toLocaleString();
    },
    platformName(id) {
      return platformLabel(id, k => this.$t(k));
    },
    statusName(s) {
      const key = SND_STATUS_LABEL_KEYS[Number(s)];
      return key ? this.$t(key) : String(s);
    },
    reasonKey(reason) {
      return reasonLabelKey(reason);
    },
    errorText(err) {
      const key = errorKey(err);
      if (key) return this.$t(key);
      // apiErrorMessage must get a bound $t (known pitfall).
      return apiErrorMessage(err, (k, v) => this.$t(k, v));
    },
    async search() {
      const [from, to] = Array.isArray(this.dateRange) ? this.dateRange : [];
      if (!isRangeAllowed(from, to)) {
        this.errorMessage = this.$t("shipped_no_deduct.errors.range_too_wide");
        return;
      }
      this.loading = true;
      this.errorMessage = "";
      try {
        const resp = await this.getShippedNoDeduct(buildQuery({
          dateFrom: from,
          dateTo: to,
          platformIds: this.platformIds,
          statuses: this.statuses
        }));
        const data = responseData(resp);
        this.orders = data.orders;
        this.generatedAt = data.generated_at;
        this.loaded = true;
        this.mappingOnly = false;
      } catch (err) {
        this.errorMessage = this.errorText(err);
      } finally {
        this.loading = false;
      }
    },
    showMappingMissing() {
      this.mappingOnly = true;
      this.activeTab = "orders";
    },
    // Footer = per-column totals of the pivot (§8), same column order as the table.
    summaryFooter() {
      const pv = this.pivot;
      return [
        this.$t("shipped_no_deduct.total"),
        "",
        ...pv.platforms.map(p => this.fmtNum(pv.totals.by_platform[p])),
        this.fmtNum(pv.totals.quantity),
        this.fmtNum(pv.totals.order_count)
      ];
    },
    // ---- Excel (§8): sheet 1 = the pivot, sheet 2 = orders ----
    exportExcel() {
      const tr = (k, v) => this.$t(k, v);
      const wb = utils.book_new();
      utils.book_append_sheet(wb, utils.aoa_to_sheet(summarySheetRows(this.filteredOrders, tr)),
        this.$t("shipped_no_deduct.sheet_summary").slice(0, 31));
      utils.book_append_sheet(wb, utils.aoa_to_sheet(orderSheetRows(this.filteredOrders, tr)),
        this.$t("shipped_no_deduct.sheet_orders").slice(0, 31));
      const [from, to] = this.dateRange || [];
      writeFile(wb, exportFileName(from, to));
    }
  }
};
</script>

<style scoped>
/* #441: one row at >= 1280px (sidebar open), wraps naturally below that. */
.snd-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 8px 12px;
}
.snd-field {
  flex: 1 1 160px;
  min-width: 160px;
  max-width: 240px;
}
.snd-field--date {
  flex: 1 1 240px;
  min-width: 240px;
  max-width: 300px;
}
.snd-field .form-control-label {
  display: block;
}
.snd-field .snd-date {
  width: 100%;
}
.snd-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 8px;
}
/* One control height for the whole row (owner: "ปุ่มให้มีขนาดเท่ากับ combobox").
   The theme pins every el-select input to $input-height
   (src/assets/sass/core/vendors/_select.scss) = 43px in a real render, whatever
   `size` says; the date editor (32px at size="small") and the buttons follow
   it here. Font sizes are unchanged. Re-measure if the theme's input height moves. */
.snd-filters .snd-date.el-date-editor {
  height: 43px;
}
.snd-actions .snd-btn {
  height: 43px;
  margin: 0;
  padding-top: 0;
  padding-bottom: 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}
.snd-subbar {
  gap: 8px;
  margin-top: 4px;
}
@media (max-width: 575.98px) {
  .snd-field,
  .snd-field--date {
    flex-basis: 100%;
    max-width: 100%;
    min-width: 0;
  }
}
.snd-link {
  cursor: pointer;
}
.snd-wrap-tag {
  white-space: normal;
  height: auto;
  line-height: 1.4;
  padding-top: 2px;
  padding-bottom: 2px;
}
</style>

<style>
/* #437 fix round 1 / #439 — fit both tables into a 1440px viewport. The theme
   pads every cell 1.5rem per side; 8px matches bon-tight on OrdersNew.
   Unscoped: el-table renders its own td/th, so a scoped rule never reaches them. */
.card .el-table.snd-orders td,
.card .el-table.snd-orders th,
.card .el-table.snd-pivot td,
.card .el-table.snd-pivot th {
  padding-left: 8px;
  padding-right: 8px;
}
.card .el-table.snd-orders .cell,
.card .el-table.snd-pivot .cell {
  padding-left: 0;
  padding-right: 0;
}
.el-table.snd-orders td.snd-nowrap .cell,
.el-table.snd-orders th.snd-nowrap .cell,
.el-table.snd-pivot td.snd-nowrap .cell,
.el-table.snd-pivot th.snd-nowrap .cell {
  white-space: nowrap;
  word-break: normal;
}
.el-table.snd-orders td.snd-word-wrap .cell,
.el-table.snd-pivot td.snd-word-wrap .cell {
  word-break: normal;
  overflow-wrap: break-word;
}
</style>
